/**
 * Meta Pixel + Conversions API — consent-gated web tracking (iter101)
 * ──────────────────────────────────────────────────────────────────
 * Retargeting / Custom Audience tracking for the FLYT website. The
 * browser Pixel and the server-side Conversions API send the SAME events
 * with a SHARED event_id so Meta deduplicates them.
 *
 * Hard rules:
 *   • WEB ONLY — never loads inside the native iOS Capacitor shell (the
 *     app uses the native Meta App Events SDK instead; see nativeBridge).
 *   • CONSENT GATED — nothing loads or fires until the user's stored
 *     consent (`flyt_cookie_consent`) is exactly 'accepted'.
 *   • Conversion events fire only AFTER the underlying action succeeds.
 *     The only exception is AppStoreClick, which fires on the outbound
 *     click itself.
 *   • The CAPI access token lives ONLY on the server — never here.
 */
import { isNativeApp } from './nativeBridge';

const PIXEL_ID = process.env.REACT_APP_META_PIXEL_ID;
const API = process.env.REACT_APP_BACKEND_URL;
const CONSENT_KEY = 'flyt_cookie_consent';

let pixelLoaded = false;
let loadPromise = null;

/** Consent + platform gate — tracking is web-only and opt-in. */
export function metaTrackingAllowed() {
  try {
    if (isNativeApp()) return false;
    if (!PIXEL_ID) return false;
    return window.localStorage.getItem(CONSENT_KEY) === 'accepted';
  } catch {
    return false;
  }
}

/** Inject fbevents.js once, with the base-snippet auto PageView disabled
 *  so the SPA controls PageView timing (no double counts). */
function loadPixel() {
  if (!metaTrackingAllowed()) return Promise.reject(new Error('meta: not allowed'));
  if (pixelLoaded) return Promise.resolve();
  if (loadPromise) return loadPromise;

  loadPromise = new Promise((resolve, reject) => {
    if (!window.fbq) {
      const fbq = function (...args) {
        if (fbq.callMethod) fbq.callMethod.apply(fbq, args);
        else fbq.queue.push(args);
      };
      fbq.push = fbq;
      fbq.loaded = true;
      fbq.version = '2.0';
      fbq.queue = [];
      window.fbq = fbq;
      window._fbq = fbq;
    }
    window.fbq('set', 'autoConfig', false, PIXEL_ID);
    window.fbq('init', PIXEL_ID);

    const s = document.createElement('script');
    s.async = true;
    s.src = 'https://connect.facebook.net/en_US/fbevents.js';
    s.onload = () => { pixelLoaded = true; resolve(); };
    s.onerror = () => reject(new Error('meta: pixel failed to load'));
    document.head.appendChild(s);
  });
  return loadPromise;
}

/** Eagerly load the pixel once consent is granted (called on app boot
 *  and on the `cookieconsent-change` event). */
export function initMetaPixel() {
  if (metaTrackingAllowed()) loadPixel().catch(() => {});
}

function newEventId(name) {
  const rnd = (window.crypto && window.crypto.randomUUID)
    ? window.crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  return `${name}-${rnd}`;
}

/**
 * Fire a Meta event on BOTH the browser Pixel and (server-side) the
 * Conversions API, sharing one event_id for deduplication.
 * @param {string} eventName  PageView | ViewContent | CompleteRegistration | AppStoreClick
 * @param {{customData?: object, email?: string}} [opts]
 */
export async function trackMeta(eventName, opts = {}) {
  if (!metaTrackingAllowed()) return null;
  const eventId = newEventId(eventName);
  const eventSourceUrl = window.location.href;
  const customData = opts.customData || {};

  try {
    await loadPixel();
    window.fbq?.(opts.custom ? 'trackCustom' : 'track', eventName, customData, { eventID: eventId });
  } catch {
    // Pixel blocked/failed — CAPI below still records the event server-side.
  }

  try {
    await fetch(`${API}/api/analytics/meta-event`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        event_name: eventName,
        event_id: eventId,
        event_source_url: eventSourceUrl,
        custom_data: customData,
        email: opts.email || null,
        consent: 'accepted',
      }),
      keepalive: true,
    });
  } catch {
    // Best-effort — analytics must never break a user flow.
  }
  return eventId;
}

/** Fires on the outbound App Store click itself (per Meta guidance). */
export function trackAppStoreClick(source) {
  return trackMeta('AppStoreClick', {
    customData: { destination: 'apple_app_store', ...(source ? { source } : {}) },
  });
}
